import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { updatePreset, palette } from '@primeuix/themes';
import { LanguageService } from './language.service';
import { SettingsService } from './settings.service';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

// Ulkoisten skriptien `order`-raja: pienempi order ajetaan ennen analytiikan alustusta, muut sen jälkeen.
const EXTERNAL_SCRIPTS_ANALYTICS_ORDER = 100;
const EXTERNAL_SCRIPT_LOAD_TIMEOUT_MS = 3000;


@Injectable({
  providedIn: 'root'
})
export class ClientInitializerService {

  clientId:string;
  language:string = 'en';
  endpoint:string;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private settingsService: SettingsService,
    
    @Inject('environment')
    private environment: {[key: string]: string}
  ) { 
    this.endpoint = this.settingsService.getEndpoint();
  }

  setClientId(id:string) {
    this.clientId = id;
  }

  applyClientStyles() {
    return new Promise<boolean>((resolve , reject) => {

      // do not apply any styles if path is preview
      if (window.location.pathname == '/preview'){
        resolve(true);
        return;
      } 

      this.language = this.languageService.getCurrentLanguage();
    
      // these can be fetched at the same time
      let promises = [this.getHeaderHtml(), this.getFooterHtml(), this.getHeaderClientCSS()];

      // the html needs to be loaded before the js can be loaded, so wait for the html and css before loading js.
      Promise.all(promises)
      .then((results) => { 
        let success = results.every(result => result === true);
        if (!success) {
          resolve(false);
        }
        this.getHeaderClientJS()
      
      // finally set the customized prime theme and hero, favicon and title attributes.
      .then(() => {
        this.getPrimeTheme();
        this.setClientSettingsStyles()
        resolve(true);

      })});
    });
  }

  applyClientStylesForCustomerSearchMask() {
    return new Promise<true>((resolve , reject) => {
      this.language = this.languageService.getCurrentLanguage();
    
      this.getPrimeTheme();
      resolve(true);
    });
  }

  getHeaderHtml() {
  
    return new Promise<boolean>((resolve , reject) => {
      this.http.get(`${this.endpoint}/Content/html/header-${this.language}`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }})
        .subscribe({
          next: (res) => {
            if (res) {
              let replaced = this.replaceLinks(res);
              let hdr = document.querySelector('#hlx-header-content');
              if (hdr) {
                hdr.innerHTML = replaced;
              }
            }
            resolve(true);
          },
          error: (err) => {
            resolve(false);
          }
      });
    });

    
  }

  getFooterHtml() {

    return new Promise<boolean>((resolve , reject) => {
      this.http.get(`${this.endpoint}/Content/html/footer-${this.language}`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }  
        })
        .subscribe({
          next: (res) => {
            if (res) {
              let replaced = this.replaceLinks(res);
              let container = document.querySelector('#hlx-footer-content');
              if (container) {
                container.innerHTML = replaced;
              }
            }
            resolve(true);
          },
          error: (err) => {
            resolve(false);
          }
      });
      
    });

    
  }
  
  getHeaderClientCSS() {

    return new Promise<boolean>((resolve , reject) => {
      this.http.get(`${this.endpoint}/Content/css/header`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      })
      .subscribe({
        next: (res) => {
          if (res) {
            let head = document.querySelector('head');
            if (head) {
              const node = document.createElement("style");
              const textnode = document.createTextNode(res);
              node.appendChild(textnode);
              head.appendChild(node);
            }
          }
          resolve(true);
        },
        error: (err) => {
          resolve(false);
        }
      });

    });

  }
  
  getHeaderClientJS() {

    return new Promise<boolean>((resolve , reject) => {
      this.http.get(`${this.endpoint}/Content/js/header`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      })
      .subscribe({
        next: (res) => {
          if (res) {
            let head = document.querySelector('head');
            if (head) {
              const node = document.createElement("script");
              const textnode = document.createTextNode(res);
              node.appendChild(textnode);
              head.appendChild(node);
            }
            resolve(true);
          }
          else {
            resolve(true);
          }
        },
        error: (err) => {
          resolve(false);
        }
      });
    });
  }

  getPrimeTheme() {

    return new Promise<boolean>((resolve , reject) => {
      this.http.get(`${this.endpoint}/Content/json/ClientPrimeTheme`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      })
      .subscribe({
        next: (res) => {
          if (res) {
            let parsed = JSON.parse(res);
                    
            if (typeof  parsed.semantic.primary == 'string' && parsed.semantic.primary.includes('palette')) {
              let palettevalue = parsed.semantic.primary.split('"')[1];
              parsed.semantic.primary = palette(palettevalue);
            }
    
            if (typeof parsed.semantic.colorScheme?.light?.surface == 'string' && parsed.semantic.colorScheme.light.surface.includes('palette')) {
              let palettevalue = parsed.semantic.colorScheme.light.surface.split('"')[1];
              parsed.semantic.colorScheme.light.surface = palette(palettevalue);
            }
          
            updatePreset(parsed);
          }
          resolve(true);
        },
        error: (err) => {
          resolve(false);
        }
      });
      
    });

    
  }

  setClientSettingsStyles() {

      let heroUrl = this.settingsService.get('app.heroUrl');
      let faviconUrl = this.settingsService.get('app.faviconUrl');
      let title = this.settingsService.get('app.title');
      
      if (title) {
        let titleObj = title.find((item:any) => item.lang == this.language);
        if (titleObj) {
          document.title = titleObj.text;
        }
      }
    
      setTimeout(() => {
        if (heroUrl) {
          let img = document.getElementById("hlxHeroImage");  
          if (img) {
            img.style.backgroundImage = `url("${heroUrl}")`;
          }
        }
        if (faviconUrl) {
          let fav:any = document.querySelector("#favicon");
          if (fav) {
            fav.href= faviconUrl;
          }
  
        }    
      },100);
      
  }

  

  replaceLinks(html:string) {
    let baseurl:string = this.settingsService.get('app.websiteUrl');
    let doc = new DOMParser().parseFromString(html, "text/html");
    
    if (baseurl) {
      let links = doc.querySelectorAll('a');
      links.forEach((link:any) => {
        let href = link.getAttribute('href');
        if (!href?.includes('http://') && !href?.includes('https://')) {
          if (!href?.includes(baseurl)) {
            if (!href?.includes('tel:') && !href?.includes('mailto:')) {
              link.setAttribute('href', `${baseurl}/${href}`);
            }
          }
        }
      });
    }
    
    html = doc.documentElement.outerHTML;
    return html;
  }

  getTermsAndConditionsHtml() {

    return this.http.get(`${this.endpoint}/Content/html/tc-${this.language}`, {
      responseType: "text",
      headers: {
        'X-Conversation-ID': uuidv4()
      }});
  }

  // Asiakaskohtaiset kolmannen osapuolen skriptit (esim. cookie-bannerit, analytiikka).
  // Tiedostot ovat HTML-muotoisia - palveluntarjoajien asennusohjeet ovat lähes aina
  // <script>/<noscript>-tageja, ei paljasta JS-koodia - joten sisältö pitää parsia ja
  // rakentaa uudelleen oikeiksi DOM-elementeiksi: suoraan innerHTML:llä lisätyt
  // <script>-tagit eivät suoriudu.

  applyExternalScriptsBookingEngine(initAnalytics: () => Promise<boolean>) {
    return this.applyExternalScripts('externalScripts-wbe-head', 'externalScripts-wbe-body', initAnalytics);
  }

  applyExternalScriptsGiftCardShop(initAnalytics: () => Promise<boolean>) {
    return this.applyExternalScripts('externalScripts-gcs-head', 'externalScripts-gcs-body', initAnalytics);
  }

  // Ajaa ulkoiset skriptit kahdessa vaiheessa analytiikan alustukseen (initAnalytics) nähden:
  // elementit, joiden `order`-attribuutti on pienempi kuin EXTERNAL_SCRIPTS_ANALYTICS_ORDER, injektoidaan ja
  // niiden lataus odotetaan ENNEN analytiikkaa (esim. gtag('set', 'linker', ...) ennen gtag('config')-kutsua).
  // Muut (order >= raja, puuttuva tai virheellinen order) injektoidaan analytiikan jälkeen.
  private async applyExternalScripts(headContentName: string, bodyContentName: string, initAnalytics: () => Promise<boolean>) {
    const [headHtml, bodyHtml] = await Promise.all([
      this.getScriptBlock(headContentName),
      this.getScriptBlock(bodyContentName)
    ]);

    const blocks = [
      { name: headContentName, target: document.head, nodes: this.parseScriptBlock(headHtml) },
      { name: bodyContentName, target: document.body, nodes: this.parseScriptBlock(bodyHtml) }
    ];

    const isBeforeAnalytics = (node: Element) =>
      this.getScriptOrder(node) < EXTERNAL_SCRIPTS_ANALYTICS_ORDER;

    for (const block of blocks) {
      await this.injectNodes(block.nodes.filter(isBeforeAnalytics), block.target);
    }

    // Analytiikan virhe ei saa estää post-skriptien injektointia eikä sovelluksen latautumista.
    let analyticsOk = true;
    try {
      analyticsOk = await initAnalytics();
    } catch (err) {
      console.error('Analytics initialization failed', err);
    }

    // Post-skriptejä ei odoteta: ne eivät saa viivästyttää sovelluksen latautumista.
    this.injectPostNodes(blocks, isBeforeAnalytics).catch(err => console.error('External script injection failed', err));

    return analyticsOk;
  }

  private async injectPostNodes(blocks: { name: string, target: HTMLElement, nodes: Element[] }[], isBeforeAnalytics: (node: Element) => boolean) {
    for (const block of blocks) {
      await this.injectNodes(block.nodes.filter(node => !isBeforeAnalytics(node)), block.target);
      if (block.nodes.length) {
        console.log(`Injected ${block.name} into ${block.target.tagName}`);
      }
    }
  }

  private getScriptBlock(contentName: string) {
    return new Promise<string>((resolve) => {
      this.http.get(`${this.endpoint}/Content/html/${contentName}`, {
        responseType: "text",
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      })
      .subscribe({
        next: (res) => resolve(res ?? ''),
        // Asiakkaalla ei ole tälle kentälle sisältöä määriteltynä - ei saa estää sovelluksen latautumista.
        error: () => resolve('')
      });
    });
  }

  // Palauttaa sisällön ylätason elementit `order`-arvon mukaan lajiteltuna (stabiili: sama order säilyttää tiedostojärjestyksen).
  // Pelkkä JS-koodi ilman <script>-kääretagia palautetaan yhtenä <script>-elementtinä ilman orderia.
  private parseScriptBlock(html: string): Element[] {
    if (!html) {
      return [];
    }
    // DOMParser luo inertin dokumentin: käsittelijät (esim. <img onerror>) ja lataukset eivät suoriudu jäsennyksen aikana,
    // joten kaikki suoritus tapahtuu vasta injectNode-kutsussa order-järjestyksessä.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const elements = [...Array.from(doc.head.children), ...Array.from(doc.body.children)];

    if (elements.length === 0) {
      if (!doc.body.textContent?.trim()) {
        return [];
      }
      const fallback = document.createElement('script');
      fallback.textContent = html;
      return [fallback];
    }

    return elements
      .sort((a, b) => this.getScriptOrder(a) - this.getScriptOrder(b));
  }

  // Puuttuva tai ei-numeerinen order tulkitaan analytiikan jälkeiseksi (sama kuin ennen order-tukea).
  private getScriptOrder(node: Element): number {
    const raw = node.getAttribute('order');
    if (raw === null || raw.trim() === '') {
      return EXTERNAL_SCRIPTS_ANALYTICS_ORDER;
    }
    const order = Number(raw);
    return Number.isFinite(order) ? order : EXTERNAL_SCRIPTS_ANALYTICS_ORDER;
  }

  // Injektoi elementit yksi kerrallaan: ulkoisen (src) skriptin lataus odotetaan ennen seuraavaa elementtiä,
  // koska inline-skripti suoritetaan heti eikä async=false takaa järjestystä src- ja inline-skriptien välillä.
  private async injectNodes(nodes: Element[], target: HTMLElement) {
    for (const node of nodes) {
      const load = this.injectNode(node, target);
      if (load) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        // Ei jäädä jumiin, jos ulkoinen skripti ei lataudu.
        await Promise.race([
          load,
          new Promise<void>(resolve => { timer = setTimeout(resolve, EXTERNAL_SCRIPT_LOAD_TIMEOUT_MS); })
        ]);
        clearTimeout(timer);
      }
    }
  }

  // Palauttaa Promisen, joka resolvaa kun ulkoinen (src) skripti on ladattu tai epäonnistunut.
  private injectNode(node: Element, target: HTMLElement): Promise<void> | null {
    if (node.tagName !== 'SCRIPT') {
      target.appendChild(document.importNode(node, true));
      return null;
    }

    const original = node as HTMLScriptElement;
    const clone = document.createElement('script');

    Array.from(original.attributes).forEach(attr =>
      clone.setAttribute(attr.name, attr.value)
    );

    if (!original.getAttribute('src')) {
      clone.textContent = original.textContent;
      target.appendChild(clone);
      return null;
    }

    // async = false säilyttää tiedostossa olevan latausjärjestyksen
    // (esim. consent-bannerin pitää ehtiä alustua ennen analytiikkaa).
    clone.async = false;

    // Osa skripteistä (esim. Fastbots embed.js) alustautuu vain `window.onload`-käsittelijällä.
    // Koska skripti lisätään dynaamisesti vasta kun load-tapahtuma on jo laukaistu,
    // käsittelijä ei koskaan suoriutuisi - ajetaan se itse, jos skripti asetti uuden.
    const previousOnload = window.onload;
    const loaded = new Promise<void>(resolve => {
      clone.addEventListener('load', () => {
        const currentOnload = window.onload;
        if (document.readyState === 'complete' && currentOnload && currentOnload !== previousOnload) {
          currentOnload.call(window, new Event('load'));
        }
        resolve();
      });
      clone.addEventListener('error', () => {
        console.warn('External script failed to load', original.getAttribute('src'));
        resolve();
      });
    });

    target.appendChild(clone);
    return loaded;
  }
}
